import { useMutation, useQuery } from '@apollo/client';
import {
    Alert,
    Button,
    Drawer,
    Space,
    Spin,
    Tag,
    Typography,
    message
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { formatTimestampToDateTime } from 'utils/dateTimeUtils';
import { formatWeightKgToLb } from 'utils/numberUtils';
import { GET_BAG_DETAIL_PAGED, GetBagDetailPagedResponse } from '../../../graphql/queries/getBagDetail';
import { GET_ALL_CARRIER_OPTIONS, GetAllCarrierOptionsResponse } from './graphql/queries/getAllCarrierOptions';
import { getCarrierName } from './utils/carrierUtils';
import { BagPrintItem, PRINT_SELECTED_TAGS, PrintSelectedTagsResponse } from './graphql/mutations/batchOperations';
import { printPdfFromBase64 } from './utils/printUtils';
import { useTranslation } from 'react-i18next';
import BagShipmentTimeline from './components/BagShipmentTimeline';
import BagShipmentSnapshotPanel from './components/BagShipmentSnapshotPanel';
import {
    GET_BAG_SHIPMENT_HISTORY,
    type GetBagShipmentHistoryResponse,
    type GetBagShipmentHistoryVariables,
} from './graphql/queries/getBagShipmentHistory';
import { getExternalBusinessTypeText } from './graphql/types/BagTagType';


const { Text } = Typography;

interface BagDetailDrawerProps {
    visible: boolean;
    bagNumber: string | null;
    onClose: () => void;
}

// Drawer 目标宽度 1200px；窗口不足时按 95% viewport 自适应，避免小屏溢出
const useDrawerWidth = (preferred = 1200, ratio = 0.95) => {
    const [width, setWidth] = useState(() =>
        typeof window === 'undefined' ? preferred : Math.min(preferred, Math.floor(window.innerWidth * ratio))
    );
    useEffect(() => {
        const onResize = () => setWidth(Math.min(preferred, Math.floor(window.innerWidth * ratio)));
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [preferred, ratio]);
    return width;
};

const BagDetailDrawer: React.FC<BagDetailDrawerProps> = ({ visible, bagNumber, onClose }) => {
    const { t } = useTranslation();
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const drawerWidth = useDrawerWidth();

    const { data, loading, error, refetch } = useQuery<GetBagDetailPagedResponse>(GET_BAG_DETAIL_PAGED, {
        variables: { bagNumber },
        skip: !bagNumber || !visible,
        errorPolicy: 'all'
    });

    const {
        data: historyData,
        loading: historyLoading,
    } = useQuery<GetBagShipmentHistoryResponse, GetBagShipmentHistoryVariables>(
        GET_BAG_SHIPMENT_HISTORY,
        {
            variables: { bagNumber: bagNumber ?? '' },
            skip: !bagNumber || !visible,
            errorPolicy: 'all',
        }
    );

    const [printSelectedTags, { loading: printing }] = useMutation<PrintSelectedTagsResponse>(PRINT_SELECTED_TAGS);

    const { data: carrierData } = useQuery<GetAllCarrierOptionsResponse>(GET_ALL_CARRIER_OPTIONS);
    const carrierOptions = carrierData?.getAllCarrierOptions || [];

    const bagDetail = data?.bagDetail;
    const shipmentNodes = useMemo(() => historyData?.bagShipmentHistory ?? [], [historyData]);

    useEffect(() => {
        if (!shipmentNodes.length) {
            setSelectedNodeId(null);
            return;
        }
        const currentNode = shipmentNodes.find((n) => n.kind === 'CURRENT');
        setSelectedNodeId((currentNode ?? shipmentNodes[shipmentNodes.length - 1]).id);
    }, [shipmentNodes]);

    const getBagStatusText = (status: number) => {
        const statusMap: Record<number, { text: string; color: string }> = {
            0: { text: t('enum.bagStatus.notUsed'), color: 'blue' },
            1: { text: t('enum.bagStatus.used'), color: 'green' },
            2: { text: t('enum.bagStatus.discarded'), color: 'grey' },
            3: { text: t('enum.bagStatus.handover'), color: 'orange' },
            4: { text: t('enum.bagStatus.closed'), color: 'red' }
        };
        return statusMap[status] || { text: t('enum.bagStatus.unknown'), color: 'default' };
    };

    const handlePrint = async () => {
        if (!bagDetail?.id) {
            message.error(t('wms.bags.messages.bagInfoIncomplete'));
            return;
        }

        const items: BagPrintItem[] = [{
            bagId: bagDetail.id.toString(),
            copies: 1
        }];

        try {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            const localTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            const result = await printSelectedTags({
                variables: {
                    items,
                    localTime,
                    timeZone
                }
            });
            if (result.data?.printSelectedTags?.success && result.data.printSelectedTags.pdfBase64) {
                printPdfFromBase64(result.data.printSelectedTags.pdfBase64, `bag_tag_${bagDetail.bagNumber}_${Date.now()}.pdf`);
                message.success(t('wms.bags.messages.bagLabelGeneratedSuccessfully'));
            } else {
                message.error(result.data?.printSelectedTags?.message || t('wms.bags.messages.bagLabelGeneratedFailed'));
            }
        } catch (error) {
            console.error('打印包牌失败:', error);
            message.error('打印包牌时发生错误');
        }
    };

    const renderContent = () => {
        if (!visible) {
            return null;
        }

        if (loading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                    <Spin size="large" />
                </div>
            );
        }

        if (error || !bagDetail) {
            return (
                <Alert
                    message={t('wms.bags.messages.loadFailed')}
                    description={error?.message || t('wms.bags.messages.bagInfoNotFound')}
                    type="error"
                    showIcon
                    action={
                        <Space>
                            <Button size="small" onClick={() => refetch()}>{t('components.button.retry')}</Button>
                            <Button size="small" onClick={onClose}>{t('components.button.close')}</Button>
                        </Space>
                    }
                />
            );
        }

        const statusInfo = getBagStatusText(bagDetail.status);

        return (
            <div style={{ padding: '5px 24px', display: 'flex', gap: 16 }}>
                {/* 左侧：基本信息 + 发运历史时间轴 */}
                <div style={{ flex: '0 0 360px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.creationHub')}</Text>
                                <Text>{bagDetail.warehouse?.name || 'Unknown'}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.createTime')}</Text>
                                <Text>{formatTimestampToDateTime(bagDetail.createdAt)}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.bagNum')}</Text>
                                <Text>{bagDetail.bagNumber}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.bagSortingCode')}</Text>
                                <Text>{bagDetail.sortingCode || '-'}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.bagStatus')}</Text>
                                <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.carrier')}</Text>
                                <Text>{getCarrierName(bagDetail.carrierCode, carrierOptions)}</Text>
                            </div>
                            {/* 装包重量：后端存 bag.weight_kg（订单累计，不含皮重）+ containerTareWeightKg（容器皮重，未绑定容器为 null）；
                                页面展示时相加得到"包裹 + 容器"总重量，再转换为 lb（工具已 i18n"lb/磅"）。
                                未称重（weightKg null/0）时不加皮重，由 formatWeightKgToLb 内部兜底为 '-'。
                                与 BagsPage.tsx 列表页 packageWeight 列口径完全一致。 */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">
                                    {t('wms.bags.pageDetails.packageWeight')}
                                </Text>
                                <Text>{(() => {
                                    const orderKg = bagDetail.weightKg ?? 0;
                                    if (orderKg <= 0) return formatWeightKgToLb(bagDetail.weightKg);
                                    return formatWeightKgToLb(orderKg + (bagDetail.containerTareWeightKg ?? 0));
                                })()}</Text>
                            </div>
                            {/* PPMOD 相关：外部业务类型 + 容器类型（含名字快照）；无值展示 '-' */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">
                                    {t('wms.bags.pageDetails.externalBusinessType') || t('wms.bags.columns.externalBusinessType') || 'External Business Type'}
                                </Text>
                                <Text>{getExternalBusinessTypeText(bagDetail.externalBusinessType, t)}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">
                                    {t('wms.bags.pageDetails.containerType') || t('wms.bags.columns.containerType') || 'Container Type'}
                                </Text>
                                <Text>{bagDetail.containerName || '-'}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.ttBizType')}</Text>
                                <Text>{bagDetail.ttBizType === 'UNLIMITED' ? t('wms.bags.pageDetails.ttBizTypeUnlimited') : (bagDetail.ttBizType || '-')}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.sortingSegmentCode')}</Text>
                                <Text>{bagDetail.sortingSegmentCode || '-'}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.lastMileSortingCode')}</Text>
                                <Text>{bagDetail.lastMileSortingCode || '-'}</Text>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            {t('wms.bags.shipmentHistory.title')}
                        </Text>
                        <BagShipmentTimeline
                            nodes={shipmentNodes}
                            selectedNodeId={selectedNodeId}
                            onSelect={setSelectedNodeId}
                            loading={historyLoading}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            type="primary"
                            onClick={handlePrint}
                            loading={printing}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('wms.bags.pageDetails.printBagLabel')}</span>
                        </Button>
                        <Button
                            onClick={onClose}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">OK</span>
                        </Button>
                    </div>
                </div>

                {/* 右侧：选中节点的运单快照 */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {bagNumber && (
                        <BagShipmentSnapshotPanel
                            bagNumber={bagNumber}
                            nodes={shipmentNodes}
                            selectedNodeId={selectedNodeId}
                            onSelectNode={setSelectedNodeId}
                        />
                    )}
                </div>
            </div>
        );
    };

    return (
        <Drawer
            title={`${t('wms.bags.pageDetails.bagDetail')} - ${bagNumber || ''}`}
            placement="right"
            onClose={onClose}
            open={visible}
            width={drawerWidth}
        >
            {renderContent()}
        </Drawer>
    );
};

export default BagDetailDrawer;
